import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Box, DisplayPosition, Overflow, ZIndex } from '@gousto-internal/citrus-react'

import { Collapsed } from './components/Collapsed'
import { EditBoxSizeModal } from './components/EditBoxSizeModal'
import { EditDeliveryAddressModal } from './components/EditDeliveryAddressModal'
import { Expanded } from './components/Expanded'
import type { OrderDetailsDropdownCloseMethod } from './tracking'
import {
  trackEditOrderDetailsAddress,
  trackEditOrderDetailsBoxSize,
  trackOrderDetailsDropdownClosed,
  trackOrderDetailsDropdownImpression,
  trackOrderDetailsDropdownOpened,
} from './tracking'
import { useCollapseOnScroll } from './useCollapseOnScroll'
import { useDeliverySettings } from './useDeliverySettings'
import { useDiscountBadge } from './useDiscountBadge'
import { useDismissOnOutside } from './useDismissOnOutside'
import { useHeightCollapseTransition } from './useHeightCollapseTransition'
import { useOrderDetailsTrackingProperties } from './useOrderDetailsTrackingProperties'
import { usePublishCssVarHeight } from './usePublishCssVarHeight'

type PanelState = {
  isExpanded: boolean
  isClosing: boolean
  isOverlayMode: boolean
}

// First collapse permanently switches into overlay mode for the session.
const INITIAL_PANEL_STATE: PanelState = {
  isExpanded: true,
  isClosing: false,
  isOverlayMode: false,
}

// TODO: split into a dumb presenter; visibility/data wiring belongs in the parent.
export const DeliverySettingsDropdown = () => {
  const { isEnabled, boxSize, deliveryDayShort, deliveryDayLong, postcode, shortAddress } =
    useDeliverySettings()
  const trackingProperties = useOrderDetailsTrackingProperties()
  const { badge: discountBadge, isSettled: isDiscountSettled } = useDiscountBadge({
    enabled: isEnabled,
  })
  // Single state object: close() fires from unbatched native listeners, where separate
  // setState calls could unmount the content before it animates.
  const [panelState, setPanelState] = useState<PanelState>(INITIAL_PANEL_STATE)
  const { isExpanded, isClosing, isOverlayMode } = panelState
  const [isEditBoxSizeModalOpen, setIsEditBoxSizeModalOpen] = useState(false)
  const [isEditDeliveryAddressModalOpen, setIsEditDeliveryAddressModalOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const expandedContentRef = useRef<HTMLDivElement>(null)

  // Refs let the unbatched native listeners below read current values without
  // re-subscribing every render.
  const trackingPropertiesRef = useRef(trackingProperties)
  trackingPropertiesRef.current = trackingProperties
  const discountBadgeRef = useRef(discountBadge)
  discountBadgeRef.current = discountBadge
  const isExpandedRef = useRef(isExpanded)
  isExpandedRef.current = isExpanded

  // Fires once per mount, once eligibility has settled and delivery_date/postcode
  // have hydrated (both default to '' before basket data loads).
  const impressionTrackedRef = useRef(false)
  useEffect(() => {
    if (
      !isEnabled ||
      !isDiscountSettled ||
      !trackingProperties.delivery_date ||
      !trackingProperties.postcode ||
      impressionTrackedRef.current
    ) {
      return
    }
    impressionTrackedRef.current = true
    const badge = discountBadgeRef.current
    trackOrderDetailsDropdownImpression({
      ...trackingPropertiesRef.current,
      banner_state: isExpandedRef.current ? 'expanded' : 'collapsed',
      discount_shown: badge !== null,
      discount_offered_pct: badge?.offeredPct ?? null,
      discount_offered_fixed: badge?.offeredFixed ?? null,
    })
  }, [isEnabled, isDiscountSettled, trackingProperties.delivery_date, trackingProperties.postcode])

  const collapsePanel = useCallback(() => {
    // isOverlayMode flips later, in onTransitionEnd, once the shrink animation finishes.
    setPanelState((prev) => ({ ...prev, isExpanded: false, isClosing: true }))
  }, [])

  // Edit-confirm collapses use collapsePanel() directly and stay silent.
  const close = useCallback(
    (method: OrderDetailsDropdownCloseMethod) => {
      const properties = trackingPropertiesRef.current
      if (properties.delivery_date && properties.postcode) {
        trackOrderDetailsDropdownClosed({ ...properties, close_method: method })
      }
      collapsePanel()
    },
    [collapsePanel],
  )

  const toggle = () => {
    if (isExpanded) {
      close('toggle')
    } else {
      trackOrderDetailsDropdownOpened(trackingProperties)
      setPanelState((prev) => ({ ...prev, isExpanded: true, isClosing: false }))
    }
  }

  const handleEditBoxSize = () => {
    trackEditOrderDetailsBoxSize()
    setIsEditBoxSizeModalOpen(true)
  }

  const handleEditAddress = () => {
    trackEditOrderDetailsAddress()
    setIsEditDeliveryAddressModalOpen(true)
  }

  const isAnyModalOpen = isEditBoxSizeModalOpen || isEditDeliveryAddressModalOpen
  const closeOnOutside = useCallback(() => close('outside_click'), [close])
  const closeOnScroll = useCallback(() => close('scroll'), [close])
  // isEnabled gates both so a stray scroll/outside-click can't close() before
  // eligibility resolves.
  useDismissOnOutside(wrapperRef, isEnabled && isExpanded && !isAnyModalOpen, closeOnOutside)
  useCollapseOnScroll(isEnabled && isExpanded && !isOverlayMode && !isAnyModalOpen, closeOnScroll)
  // Consumed by MenuPage's sticky NavBar to offset its top.
  usePublishCssVarHeight(wrapperRef, '--delivery-settings-dropdown-height', isEnabled)
  const collapseTransition = useHeightCollapseTransition(expandedContentRef, isExpanded)

  if (!isEnabled) return null

  const layoutMode = isOverlayMode ? 'overlay' : 'push'

  return (
    // Sticky even in push mode, so scroll doesn't carry it away before it can animate.
    <Box ref={wrapperRef} zIndex={ZIndex.Dropdown} top="0" style={{ position: 'sticky' }}>
      <Collapsed
        boxSize={boxSize}
        deliveryDay={deliveryDayShort}
        address={postcode}
        isExpanded={isExpanded}
        onEditClick={toggle}
        discountText={discountBadge?.collapsedText}
      />
      {/* Kept mounted — losing the ref would break height measurement on reopen. */}
      <Box
        ref={expandedContentRef}
        position={isOverlayMode ? DisplayPosition.Absolute : DisplayPosition.Relative}
        top={isOverlayMode ? '100%' : undefined}
        left={isOverlayMode ? '0' : undefined}
        right={isOverlayMode ? '0' : undefined}
        overflowY={Overflow.Hidden}
        data-testid="delivery-settings-expanded-panel"
        data-layout-mode={layoutMode}
        boxShadow={isOverlayMode ? '0px 12px 24px rgba(0, 0, 0, 0.18)' : undefined}
        style={collapseTransition}
        onTransitionEnd={() => {
          setPanelState((prev) =>
            prev.isExpanded ? prev : { ...prev, isClosing: false, isOverlayMode: true },
          )
        }}
      >
        {(isExpanded || isClosing) && (
          <Expanded
            boxSize={boxSize}
            deliveryDay={deliveryDayLong}
            address={shortAddress}
            onEditBoxSize={handleEditBoxSize}
            onEditAddress={handleEditAddress}
            onRowConfirmed={collapsePanel}
            onClose={() => close('toggle')}
            discountText={discountBadge?.expandedText}
          />
        )}
      </Box>
      {isEditBoxSizeModalOpen && (
        <EditBoxSizeModal
          isOpen
          onClose={() => setIsEditBoxSizeModalOpen(false)}
          onConfirmComplete={() => {
            setIsEditBoxSizeModalOpen(false)
            collapsePanel()
          }}
        />
      )}
      {isEditDeliveryAddressModalOpen && (
        <EditDeliveryAddressModal
          isOpen
          onClose={() => setIsEditDeliveryAddressModalOpen(false)}
          onConfirmComplete={() => {
            setIsEditDeliveryAddressModalOpen(false)
            collapsePanel()
          }}
        />
      )}
    </Box>
  )
}
